import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Key, Copy, Check, Terminal, Users, Zap, Shield, AlertCircle, ChevronRight,
  Globe, Code, List, BookOpen, RefreshCw, ArrowUpRight,
  ShieldCheck, Info, FileText, Activity, Search, TrendingUp,
  User, Building2, FolderOpen, Mail, MessageSquare
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import DashboardModuleShell from '../components/DashboardModuleShell';
import { cn } from '../utils/cn';

const NAV = [
  { id: 'overview', label: 'API Overview', Icon: BookOpen },
  { id: 'auth', label: 'Authentication', Icon: Key },
  { id: 'leads', label: 'Leads', Icon: Users },
  { id: 'deals', label: 'Deals & Pipeline', Icon: TrendingUp },
  { id: 'contacts', label: 'Contacts', Icon: User },
  { id: 'accounts', label: 'Accounts', Icon: Building2 },
  { id: 'activities', label: 'Activities & Tasks', Icon: Activity },
  { id: 'templates', label: 'Templates', Icon: Mail },
  { id: 'files', label: 'Files & Media', Icon: FolderOpen },
  { id: 'marketing', label: 'Marketing & Forms', Icon: Globe },
  { id: 'whatsapp', label: 'WhatsApp API', Icon: MessageSquare },
  { id: 'errors', label: 'Response Codes', Icon: AlertCircle },
];

const MethodBadge = ({ method }) => {
  const styles = {
    GET: 'bg-[#EEF4FF] text-[#0176D3] border-[#D8E6FE]',
    POST: 'bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]',
    PUT: 'bg-[#FEF1E5] text-[#DD7A01] border-[#FDE4C9]',
    DELETE: 'bg-[#FEF1EE] text-[#BA0517] border-[#FDDDE3]',
    PATCH: 'bg-[#EEF4FF] text-[#0176D3] border-[#D8E6FE]',
  };
  return (
    <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase border shrink-0", styles[method] || 'bg-[#F7F9FB] text-[#5C5C5C] border-[#F3F2F2]')}>
      {method}
    </span>
  );
};

const EndpointRow = ({ method, path, desc }) => (
  <div className="flex items-center gap-4 py-2.5 px-3 rounded hover:bg-[#F7F9FB] transition-all border border-transparent hover:border-[#F3F2F2] group">
    <MethodBadge method={method} />
    <code className="text-[12px] font-mono text-[#444444] tracking-tight group-hover:text-[#0176D3] transition-colors">{path}</code>
    <span className="text-[11px] text-[#706E6B] ml-auto opacity-0 group-hover:opacity-100 transition-opacity shrink-0">{desc}</span>
  </div>
);

function CodeTabs({ examples }) {
  const [lang, setLang] = useState('cURL');
  const [copied, setCopied] = useState(false);
  const code = examples[lang] || '';
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden mb-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#F3F2F2] bg-[#F7F9FB]">
        <div className="flex gap-2">
          {Object.keys(examples).map(l => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={cn(
 "text-[13px] font-semibold transition-all py-1 px-3 rounded",
 lang === l ? 'bg-white text-[#0176D3] shadow-sm border border-[#E5E5E5]' : 'text-[#706E6B] hover:text-[#444444]'
 )}
            >
              {l}
            </button>
          ))}
        </div>
        <button onClick={copy} className="flex items-center gap-2 text-[11px] font-bold text-[#706E6B] hover:text-[#0176D3] transition-colors">
          {copied ? <Check size={14} className="text-[#2E844A]" /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="p-6 bg-[#001639] relative overflow-hidden">
        <pre className="overflow-x-auto scrollbar-hide relative z-10"><code className="text-[13px] font-mono text-[#D8E6FE] leading-relaxed block">{code}</code></pre>
      </div>
    </div>
  );
}

function ParamsTable({ rows }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden mb-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="slds2-table-wrap overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-[#F7F9FB] border-b border-[#E5E5E5]">
            <th className="px-4 py-2.5 text-[11px] font-bold uppercase text-[#706E6B]">Parameter</th>
            <th className="px-4 py-2.5 text-[11px] font-bold uppercase text-[#706E6B]">Type</th>
            <th className="px-4 py-2.5 text-[11px] font-bold uppercase text-[#706E6B]">Required</th>
            <th className="px-4 py-2.5 text-[11px] font-bold uppercase text-[#706E6B]">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F3F2F2]">
          {rows.map(([name, type, req, desc]) => (
            <tr key={name} className="hover:bg-[#F7F9FB] transition-colors">
              <td className="px-4 py-2.5">
                <code className="text-[12px] font-mono text-[#0176D3] font-bold">{name}</code>
              </td>
              <td className="px-4 py-2.5">
                <span className="px-2 py-0.5 rounded bg-[#F3F2F2] text-[#5C5C5C] text-[10px] font-bold uppercase">{type}</span>
              </td>
              <td className="px-4 py-2.5">
                <span className={cn("text-[10px] font-bold uppercase", req ? "text-[#BA0517]" : "text-[#706E6B]")}>
                  {req ? 'Required' : 'Optional'}
                </span>
              </td>
              <td className="px-4 py-2.5">
                <p className="text-[13px] text-[#5C5C5C] leading-relaxed">{desc}</p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ResponseBox({ json }) {
  return (
    <div className="bg-[#080707] border border-[#444444] rounded-lg overflow-hidden mb-8 shadow-inner">
      <div className="px-6 py-2 border-b border-[#444444] flex items-center justify-between bg-[#080707]">
        <div className="text-[10px] font-bold text-[#706E6B] uppercase ">Example Response</div>
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#2E844A]/40" />
          <div className="w-2 h-2 rounded-full bg-[#DD7A01]/40" />
          <div className="w-2 h-2 rounded-full bg-[#BA0517]/40" />
        </div>
      </div>
      <div className="p-6">
        <pre className="text-[12px] font-mono leading-relaxed text-[#2E844A] overflow-x-auto scrollbar-hide">
          <code>{JSON.stringify(json, null, 2)}</code>
        </pre>
      </div>
    </div>
  );
}

function TryItPanel({ method, path, defaultBody }) {
  const BASE = window.location.origin + '/api';
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(BASE + path);
  const [body, setBody] = useState(defaultBody ? JSON.stringify(defaultBody, null, 2) : '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const token = localStorage.getItem('token');

  const sendRequest = async () => {
    setLoading(true);
    setResult(null);
    const t0 = Date.now();
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const opts = { method, headers };
      if (['POST', 'PUT', 'PATCH'].includes(method) && body.trim()) {
        try { JSON.parse(body); opts.body = body; }
        catch { setResult({ status: 0, data: { error: 'Invalid JSON payload' }, elapsed: 0, ok: false }); setLoading(false); return; }
      }
      const res = await fetch(url, opts);
      let data;
      try { data = await res.json(); } catch { data = { error: 'Failed to parse response' }; }
      setResult({ status: res.status, data, elapsed: Date.now() - t0, ok: res.ok });
    } catch (err) {
      setResult({ status: 0, data: { error: err.message }, elapsed: Date.now() - t0, ok: false });
    }
    setLoading(false);
  };

  return (
    <div className="mb-12">
      <Button
        onClick={() => { setOpen(!open); setResult(null); }}
        variant={open ? "secondary" : "outline"}
        className="h-10 px-6 font-bold text-[12px] tracking-wide"
        icon={RefreshCw}
      >
        {open ? 'Close Console' : 'Try this endpoint →'}
      </Button>

      {open && (
        <div className="mt-4 bg-white border border-[#E5E5E5] rounded-lg p-6 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[11px] font-semibold text-[#706E6B] ml-0.5">Request URL</label>
              <div className="flex gap-2">
                <div className={cn(
 "px-4 flex items-center rounded border font-bold text-[12px]",
 method === 'GET' ? 'bg-[#EEF4FF] text-[#0176D3] border-[#D8E6FE]' : 'bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]'
 )}>
                  {method}
                </div>
                <input
                  className="flex-1 bg-[#F7F9FB] border border-[#D8DDE6] rounded h-9 px-4 font-mono text-[13px] text-[#444444] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                />
              </div>
            </div>

            {['POST', 'PUT', 'PATCH'].includes(method) && (
              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B] ml-0.5">Request Body (JSON)</label>
                <textarea
                  className="w-full bg-white border border-[#D8DDE6] rounded p-4 font-mono text-[12px] text-[#444444] outline-none focus:border-[#0176D3] min-h-[120px] resize-none shadow-inner focus:ring-1 focus:ring-[#0176D3]"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                />
              </div>
            )}

            <Button variant="brand" className="w-full h-12 text-sm font-bold shadow-sm shadow-blue-500/20" onClick={sendRequest} disabled={loading} loading={loading}>
              Send Request
            </Button>

            {result && (
              <div className="pt-6 border-t border-[#F3F2F2] animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center gap-4 mb-4">
                  <span className={cn(
 "px-3 py-1 rounded text-[11px] font-bold border",
 result.ok ? "bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]" : "bg-[#FEF1EE] text-[#BA0517] border-[#FDDDE3]"
 )}>
                    {result.status} {result.ok ? 'OK' : 'Error'}
                  </span>
                  <span className="text-[11px] font-medium text-[#706E6B] font-mono italic">{result.elapsed}ms</span>
                </div>
                <div className="bg-[#080707] p-6 rounded border border-[#444444] max-h-[300px] overflow-y-auto scrollbar-hide">
                  <pre className="text-[12px] font-mono leading-relaxed" style={{ color: result.ok ? '#34d399' : '#f87171' }}>
                    <code>{JSON.stringify(result.data, null, 2)}</code>
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ id, icon: Icon, color, title, subtitle }) {
  return (
    <div className="flex items-center gap-4">
      <div className={cn("w-12 h-12 rounded flex items-center justify-center border", color)}>
        <Icon size={24} />
      </div>
      <div>
        <h2 className="text-3xl font-bold text-[#080707] tracking-tight">{title}</h2>
        <p className="text-sm text-[#706E6B] font-medium">{subtitle}</p>
      </div>
    </div>
  );
}

export default function ApiDocsPage() {
  const { user } = useSelector(state => state.auth);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const sectionRefs = useRef({});
  const BASE = window.location.origin + '/api';
  const API_KEY = user?.apiKey || 'YOUR_API_KEY';

  const copyApiKey = () => {
    if (!user?.apiKey) return;
    navigator.clipboard.writeText(user.apiKey);
    setApiKeyCopied(true);
    setTimeout(() => setApiKeyCopied(false), 2000);
  };

  const scrollTo = (id) => sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const sec = (id) => (el) => { if (el) sectionRefs.current[id] = el; };

  return (
    <DashboardModuleShell
      title="CRM API Reference"
      subtitle="Complete developer documentation for integrating with your Lead Management CRM."
      icon={Code}
    >
      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-200px)] gap-8 pb-12">

        {/* Navigation Sidebar */}
        <Card className="w-full lg:w-[280px] p-2 shrink-0 space-y-0.5 h-fit lg:sticky lg:top-4">
          <div className="px-4 py-3 mb-1 text-[11px] font-bold text-[#706E6B] uppercase border-b border-[#F3F2F2]">
            CRM API Reference
          </div>
          {NAV.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="w-full flex items-center gap-3 px-4 h-9 rounded text-[#5C5C5C] hover:bg-[#F7F9FB] hover:text-[#0176D3] transition-all group text-left"
            >
              <Icon size={16} className="shrink-0" />
              <div className="text-[13px] font-medium">{label}</div>
              <ChevronRight size={12} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}

          <div className="mt-6 pt-4 border-t border-[#F3F2F2] px-2 space-y-3">
            <div className="px-2 text-[11px] font-bold text-[#706E6B] uppercase ">Your API Key</div>
            <div onClick={copyApiKey} className="p-3 rounded bg-[#F7F9FB] border border-[#E5E5E5] flex items-center justify-between group cursor-pointer hover:border-[#0176D3]/30 transition-all">
              <div className="space-y-0.5 overflow-hidden">
                <div className="text-[10px] text-[#706E6B] uppercase font-bold ">Bearer Token Key</div>
                <code className="text-[11px] font-mono text-[#444444] truncate block">
                  {user?.apiKey ? `${user.apiKey.slice(0, 16)}…` : '(none configured)'}
                </code>
              </div>
              <div className={cn(
 "w-7 h-7 rounded shrink-0 border flex items-center justify-center transition-all ml-2",
 apiKeyCopied ? "bg-[#EBF7E6] border-[#CDEFC4] text-[#2E844A]" : "bg-white border-[#E5E5E5] text-[#706E6B] group-hover:text-[#0176D3]"
 )}>
                {apiKeyCopied ? <Check size={14} /> : <Copy size={14} />}
              </div>
            </div>
          </div>
        </Card>

        {/* Documentation Content */}
        <div className="flex-1 w-full min-w-0 space-y-24">

          {/* Overview */}
          <section ref={sec('overview')} className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-[#080707] tracking-tight">CRM API Reference</h1>
              <p className="text-lg text-[#5C5C5C] leading-relaxed max-w-3xl font-medium">
                Programmatically access and manage your Leads, Deals, Accounts, Contacts, Activities, Files, and Marketing Campaigns using our RESTful JSON API.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: 'Base API URL', val: BASE, icon: Globe, color: 'text-[#0176D3]' },
                { label: 'Auth Method', val: 'Bearer JWT Token', icon: ShieldCheck, color: 'text-[#2E844A]' },
                { label: 'Format', val: 'JSON UTF-8', icon: FileText, color: 'text-[#DD7A01]' },
                { label: 'API Version', val: 'v1 Stable', icon: Activity, color: 'text-[#0176D3]' }
              ].map((item, i) => (
                <Card key={i} className="p-6 flex items-start gap-4 hover:border-[#0176D3]/30 transition-all group border-[#E5E5E5] shadow-sm">
                  <div className={cn("w-10 h-10 rounded-lg bg-[#F7F9FB] flex items-center justify-center group-hover:bg-white transition-colors", item.color)}>
                    <item.icon size={20} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-[#706E6B] block">{item.label}</label>
                    <div className="text-[13px] font-bold text-[#444444] font-mono">{item.val}</div>
                  </div>
                </Card>
              ))}
            </div>

            <Card className="overflow-hidden border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <div className="px-6 py-4 bg-[#F7F9FB] border-b border-[#E5E5E5] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <List size={18} className="text-[#0176D3]" />
                  <h3 className="text-[14px] font-bold text-[#2E2E2E] ">Quick Endpoint Index</h3>
                </div>
                <Badge variant="blue">CRM v1</Badge>
              </div>
              <div className="p-4 space-y-0.5">
                <EndpointRow method="GET" path="/crm/leads" desc="List all leads" />
                <EndpointRow method="POST" path="/crm/leads" desc="Create new lead" />
                <EndpointRow method="POST" path="/crm/leads/import" desc="CSV bulk import" />
                <EndpointRow method="GET" path="/crm/leads/export" desc="Download leads CSV" />
                <EndpointRow method="GET" path="/crm/deals" desc="List deals pipeline" />
                <EndpointRow method="POST" path="/crm/deals" desc="Create deal" />
                <EndpointRow method="POST" path="/crm/deals/import" desc="Bulk import deals" />
                <EndpointRow method="GET" path="/crm/deals/export" desc="Export deals CSV" />
                <EndpointRow method="GET" path="/contacts/crm" desc="List CRM contacts" />
                <EndpointRow method="POST" path="/contacts/crm-import" desc="Import contacts" />
                <EndpointRow method="GET" path="/contacts/crm-export" desc="Export contacts CSV" />
                <EndpointRow method="GET" path="/crm/accounts" desc="List accounts" />
                <EndpointRow method="POST" path="/crm/accounts/import" desc="Import accounts" />
                <EndpointRow method="GET" path="/crm/accounts/export" desc="Export accounts CSV" />
                <EndpointRow method="GET" path="/crm/activities" desc="List CRM activities" />
                <EndpointRow method="GET" path="/crm/tasks" desc="List tasks" />
                <EndpointRow method="GET" path="/crm/templates" desc="Custom templates" />
                <EndpointRow method="GET" path="/crm/files" desc="Media library" />
                <EndpointRow method="GET" path="/crm/marketing" desc="Marketing config" />
                <EndpointRow method="POST" path="/v1/meta/messages/send" desc="Send WhatsApp message" />
                <EndpointRow method="POST" path="/v1/meta/campaigns/create" desc="Trigger WhatsApp campaign" />
              </div>
            </Card>
          </section>

          {/* Auth */}
          <section ref={sec('auth')} className="space-y-8">
            <SectionHeader id="auth" icon={Key} color="bg-[#FEF1E5] text-[#DD7A01] border-[#FDE4C9]" title="Authentication" subtitle="Bearer JWT token required on all endpoints" />

            <p className="text-[#5C5C5C] text-base leading-relaxed font-medium max-w-3xl border-l-4 border-[#FDE4C9] pl-6 py-2">
              All CRM API requests must include a valid JWT <code className="bg-[#F3F2F2] px-2 py-0.5 rounded text-[#DD7A01] font-bold">Authorization</code> header obtained from the login endpoint. Tokens expire after 24 hours.
            </p>

            <CodeTabs examples={{
              'cURL': `# Step 1: Obtain a token
curl -X POST ${BASE}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@domain.com","password":"yourpassword"}'

# Step 2: Use the token on CRM endpoints
curl -X GET ${BASE}/crm/leads \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json"`,
              'Node.js': `const axios = require('axios');

// Login
const login = await axios.post('${BASE}/auth/login', {
  email: 'you@domain.com',
  password: 'yourpassword'
});
const token = login.data.token;

// Use on CRM endpoints
const leads = await axios.get('${BASE}/crm/leads', {
  headers: { Authorization: \`Bearer \${token}\` }
});
console.log(leads.data);`,
            }} />

            <ResponseBox json={{ success: true, token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9…', user: { id: '64c1…', email: 'you@domain.com', role: 'admin' } }} />
          </section>

          {/* Leads */}
          <section ref={sec('leads')} className="space-y-8">
            <SectionHeader icon={Users} color="bg-[#EEF4FF] text-[#0176D3] border-[#D8E6FE]" title="Leads" subtitle="Full lead lifecycle management with scoring and enrichment" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/crm/leads" desc="Paginated list with filters" />
              <EndpointRow method="POST" path="/crm/leads" desc="Create lead with auto-scoring" />
              <EndpointRow method="GET" path="/crm/leads/:id" desc="Get single lead details" />
              <EndpointRow method="PUT" path="/crm/leads/:id" desc="Update lead record" />
              <EndpointRow method="DELETE" path="/crm/leads/:id" desc="Delete lead permanently" />
              <EndpointRow method="POST" path="/crm/leads/:id/convert" desc="Convert lead to deal" />
              <EndpointRow method="GET" path="/crm/leads/stats" desc="Aggregate KPI stats" />
              <EndpointRow method="POST" path="/crm/leads/import" desc="Bulk CSV import with enrichment" />
              <EndpointRow method="GET" path="/crm/leads/export" desc="Export all leads as CSV" />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#2E2E2E] flex items-center gap-3">
                <Terminal size={18} className="text-[#706E6B]" /> Create Lead — POST /crm/leads
              </h3>
              <ParamsTable rows={[
                ['name', 'String', true, 'Full name of the lead'],
                ['phoneNumber', 'String', false, 'Phone number including country code'],
                ['email', 'String', false, 'Email address (triggers enrichment if provided)'],
                ['source', 'Enum', false, 'Origin: web_form | manual | bulk_import | api | referral'],
                ['status', 'Enum', false, 'Status: new | contacted | qualified | converted | lost'],
                ['expectedValue', 'Number', false, 'Estimated deal value in your currency'],
                ['notes', 'String', false, 'Internal notes or details'],
                ['tags', 'String', false, 'Comma-separated tag labels'],
              ]} />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#2E2E2E]">Import CSV — POST /crm/leads/import</h3>
              <div className="p-4 bg-[#EEF4FF] border border-[#D8E6FE] rounded-lg flex items-start gap-3">
                <Info size={18} className="text-[#0176D3] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[13px] text-[#444444] font-medium">Upload a multipart/form-data file with field name <code className="bg-white px-1 rounded-lg border text-[#0176D3] font-bold">file</code>. CSV must have headers: <code className="bg-white px-1 rounded-lg border text-[#444444] font-bold">Name, Phone, Email, Source, Status, Notes</code></p>
                  <p className="text-[12px] text-[#706E6B] mt-1">Each imported lead is automatically scored and enriched based on email domain lookup.</p>
                </div>
              </div>
              <CodeTabs examples={{
                'cURL': `curl -X POST ${BASE}/crm/leads/import \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -F "file=@/path/to/leads.csv"`,
              }} />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#2E2E2E]">Export CSV — GET /crm/leads/export</h3>
              <CodeTabs examples={{
                'cURL': `curl -X GET "${BASE}/crm/leads/export" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -o leads_export.csv`,
                'Node.js': `const res = await axios.get('${BASE}/crm/leads/export', {
  headers: { Authorization: 'Bearer YOUR_JWT_TOKEN' },
  responseType: 'blob'
});
// Save file…`,
              }} />
            </div>

            <ResponseBox json={{ success: true, leads: [{ _id: '64c1…', name: 'Rahul Sharma', status: 'qualified', score: 78, leadQuality: 'high', source: 'web_form', email: 'rahul@acmecorp.com' }], total: 147 }} />

            <TryItPanel method="GET" path="/crm/leads" />
          </section>

          {/* Deals */}
          <section ref={sec('deals')} className="space-y-8">
            <SectionHeader icon={TrendingUp} color="bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]" title="Deals & Pipeline" subtitle="Deal lifecycle, stages, and Kanban board data" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/crm/deals" desc="List deals with pagination" />
              <EndpointRow method="POST" path="/crm/deals" desc="Create a new deal" />
              <EndpointRow method="GET" path="/crm/deals/:id" desc="Get deal with activities" />
              <EndpointRow method="PUT" path="/crm/deals/:id" desc="Update deal fields" />
              <EndpointRow method="DELETE" path="/crm/deals/:id" desc="Delete deal" />
              <EndpointRow method="PUT" path="/crm/deals/:id/stage" desc="Move deal to another stage" />
              <EndpointRow method="POST" path="/crm/deals/:id/won" desc="Mark deal as won" />
              <EndpointRow method="POST" path="/crm/deals/:id/lost" desc="Mark deal as lost" />
              <EndpointRow method="GET" path="/crm/deals/kanban/:pipelineId" desc="Kanban board data" />
              <EndpointRow method="POST" path="/crm/deals/import" desc="Bulk import deals CSV" />
              <EndpointRow method="GET" path="/crm/deals/export" desc="Export deals as CSV" />
            </div>

            <ParamsTable rows={[
              ['title', 'String', true, 'Deal name or opportunity title'],
              ['value', 'Number', false, 'Monetary value of the deal'],
              ['status', 'Enum', false, 'open | won | lost'],
              ['stage', 'String', false, 'Pipeline stage name'],
              ['pipelineId', 'ObjectId', false, 'Reference to a pipeline configuration'],
              ['leadId', 'ObjectId', false, 'Associated lead record ID'],
            ]} />

            <ResponseBox json={{ success: true, deal: { _id: '64d2…', title: 'Enterprise SaaS License', value: 85000, status: 'open', stage: 'proposal', lastActivity: '2026-06-29T00:00:00Z' } }} />

            <TryItPanel method="GET" path="/crm/deals" />
          </section>

          {/* Contacts */}
          <section ref={sec('contacts')} className="space-y-8">
            <SectionHeader icon={User} color="bg-[#EEF4FF] text-[#0176D3] border-[#D8E6FE]" title="Contacts" subtitle="CRM contact records linked to leads and accounts" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/contacts/crm" desc="List contacts with filters" />
              <EndpointRow method="POST" path="/contacts/crm" desc="Create contact" />
              <EndpointRow method="GET" path="/contacts/crm/:id" desc="Get contact with activities" />
              <EndpointRow method="PUT" path="/contacts/crm/:id" desc="Update contact" />
              <EndpointRow method="DELETE" path="/contacts/crm/:id" desc="Delete contact" />
              <EndpointRow method="POST" path="/contacts/crm-import" desc="Bulk import contacts CSV" />
              <EndpointRow method="GET" path="/contacts/crm-export" desc="Export contacts CSV" />
            </div>

            <ParamsTable rows={[
              ['name', 'String', true, 'Full name of the contact'],
              ['phoneNumber', 'String', false, 'Contact phone number'],
              ['email', 'String', false, 'Contact email address'],
              ['jobTitle', 'String', false, 'Job title or position'],
              ['accountId', 'ObjectId', false, 'Link to a Company/Account record'],
              ['notes', 'String', false, 'Free-form notes about this contact'],
            ]} />

            <ResponseBox json={{ success: true, contacts: [{ _id: '64e3…', name: 'Priya Nair', email: 'priya@globex.com', jobTitle: 'VP Sales' }], total: 52 }} />

            <TryItPanel method="GET" path="/contacts/crm" />
          </section>

          {/* Accounts */}
          <section ref={sec('accounts')} className="space-y-8">
            <SectionHeader icon={Building2} color="bg-[#EEF4FF] text-[#0176D3] border-[#D8E6FE]" title="Accounts" subtitle="Company and organization management" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/crm/accounts" desc="List accounts" />
              <EndpointRow method="POST" path="/crm/accounts" desc="Create account" />
              <EndpointRow method="GET" path="/crm/accounts/:id" desc="Get account with contacts" />
              <EndpointRow method="PUT" path="/crm/accounts/:id" desc="Update account" />
              <EndpointRow method="DELETE" path="/crm/accounts/:id" desc="Delete account" />
              <EndpointRow method="POST" path="/crm/accounts/import" desc="Bulk import accounts CSV" />
              <EndpointRow method="GET" path="/crm/accounts/export" desc="Export accounts CSV" />
            </div>

            <ParamsTable rows={[
              ['name', 'String', true, 'Company or organization name'],
              ['website', 'String', false, 'Corporate website URL'],
              ['email', 'String', false, 'Primary contact email'],
              ['phone', 'String', false, 'Main phone number'],
              ['industry', 'String', false, 'Industry vertical (e.g., SaaS, Healthcare)'],
              ['status', 'Enum', false, 'active | inactive | prospect'],
            ]} />

            <ResponseBox json={{ success: true, account: { _id: '64f4…', name: 'Globex Corporation', industry: 'Technology', status: 'active' }, contacts: [], activities: [] }} />

            <TryItPanel method="GET" path="/crm/accounts" />
          </section>

          {/* Activities & Tasks */}
          <section ref={sec('activities')} className="space-y-8">
            <SectionHeader icon={Activity} color="bg-[#FEF1E5] text-[#DD7A01] border-[#FDE4C9]" title="Activities & Tasks" subtitle="Track all CRM interactions and scheduled tasks" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/crm/activities" desc="Recent activities timeline" />
              <EndpointRow method="POST" path="/crm/activities" desc="Log a new activity" />
              <EndpointRow method="GET" path="/crm/tasks" desc="List tasks" />
              <EndpointRow method="POST" path="/crm/tasks" desc="Create a task" />
              <EndpointRow method="PUT" path="/crm/tasks/:id" desc="Update task status" />
              <EndpointRow method="DELETE" path="/crm/tasks/:id" desc="Delete task" />
            </div>

            <ParamsTable rows={[
              ['type', 'Enum', true, 'Activity type: call | email | meeting | note | deal_created'],
              ['title', 'String', true, 'Short description of the activity'],
              ['leadId', 'ObjectId', false, 'Related lead (optional)'],
              ['dealId', 'ObjectId', false, 'Related deal (optional)'],
              ['accountId', 'ObjectId', false, 'Related account (optional)'],
              ['completedAt', 'ISO8601', false, 'Timestamp when activity occurred'],
            ]} />

            <ResponseBox json={{ success: true, activities: [{ _id: '64g5…', type: 'call', title: 'Discovery call with Rahul', createdAt: '2026-06-29T00:00:00Z' }] }} />
          </section>

          {/* Templates */}
          <section ref={sec('templates')} className="space-y-8">
            <SectionHeader icon={Mail} color="bg-[#FEF1EE] text-[#BA0517] border-[#FDDDE3]" title="Templates" subtitle="Email and SMS campaign template management" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/crm/templates" desc="List all custom templates" />
              <EndpointRow method="POST" path="/crm/templates" desc="Create a template" />
              <EndpointRow method="PUT" path="/crm/templates/:id" desc="Update template content" />
              <EndpointRow method="DELETE" path="/crm/templates/:id" desc="Delete template" />
            </div>

            <ParamsTable rows={[
              ['name', 'String', true, 'Template display name'],
              ['type', 'Enum', true, 'email | sms'],
              ['subject', 'String', false, 'Email subject line (email type only)'],
              ['content', 'String', true, 'HTML body for email, plain text for SMS. Supports {{leadName}}, {{companyName}} placeholders'],
            ]} />

            <ResponseBox json={{ success: true, template: { _id: '64h6…', name: 'Welcome Email', type: 'email', subject: 'Welcome, {{leadName}}!', content: '<h1>Hi {{leadName}}</h1><p>Welcome aboard!</p>' } }} />

            <TryItPanel method="GET" path="/crm/templates" />
          </section>

          {/* Files */}
          <section ref={sec('files')} className="space-y-8">
            <SectionHeader icon={FolderOpen} color="bg-[#EEF4FF] text-[#0176D3] border-[#D8E6FE]" title="Files & Media Library" subtitle="Upload and version documents connected to ImageKit" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/crm/files" desc="List all uploaded files" />
              <EndpointRow method="POST" path="/crm/files/upload" desc="Upload a new file to ImageKit" />
              <EndpointRow method="POST" path="/crm/files/:id/version" desc="Upload new version of existing file" />
              <EndpointRow method="DELETE" path="/crm/files/:id" desc="Delete file record" />
            </div>

            <div className="p-4 bg-[#EEF4FF] border border-[#D8E6FE] rounded-lg flex items-start gap-3">
              <Info size={18} className="text-[#0176D3] mt-0.5 shrink-0" />
              <div>
                <p className="text-[13px] text-[#444444] font-medium">All file uploads use <code className="bg-white px-1 rounded-lg border text-[#0176D3] font-bold">multipart/form-data</code> with field name <code className="bg-white px-1 rounded-lg border text-[#444444] font-bold">file</code>. Files are stored in your configured ImageKit account under <code className="bg-white px-1 rounded-lg border text-[#444444] font-bold">/tenants/{'{companyId}'}/crm-files/</code>. Maximum file size: 25MB.</p>
              </div>
            </div>

            <CodeTabs examples={{
              'cURL': `# Upload a new file
curl -X POST "${BASE}/crm/files/upload" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -F "file=@/path/to/document.pdf"

# Upload new version of existing file  
curl -X POST "${BASE}/crm/files/FILE_ID/version" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -F "file=@/path/to/document_v2.pdf"`,
            }} />

            <ResponseBox json={{ success: true, file: { _id: '64i7…', name: 'proposal_v2.pdf', fileUrl: 'https://ik.imagekit.io/...', fileType: 'document', size: 248320, version: 2, versions: [{ version: 1 }, { version: 2 }] } }} />
          </section>

          {/* Marketing */}
          <section ref={sec('marketing')} className="space-y-8">
            <SectionHeader icon={Globe} color="bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]" title="Marketing & Lead Forms" subtitle="Configure web capture forms and lead intake settings" />

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="GET" path="/crm/marketing/config" desc="Get current lead form config" />
              <EndpointRow method="PUT" path="/crm/marketing/config" desc="Update form settings" />
              <EndpointRow method="POST" path="/crm/marketing/submit" desc="Public form submission endpoint" />
            </div>

            <div className="p-4 bg-[#EBF7E6] border border-[#CDEFC4] rounded-lg">
              <p className="text-[13px] text-[#22683E] font-medium">
                <strong>Public Endpoint:</strong> The <code className="bg-white px-1 rounded-lg border border-[#CDEFC4] font-bold">/crm/marketing/submit</code> endpoint is publicly accessible (no auth required) and is designed to receive submissions from your embedded website lead capture form. Embed the form via <code className="font-bold">/public-form.html</code>.
              </p>
            </div>

            <ResponseBox json={{ success: true, message: 'Lead captured and scored successfully', leadId: '64j8…', score: 65, leadQuality: 'medium' }} />
          </section>

          {/* WhatsApp API */}
          <section ref={sec('whatsapp')} className="space-y-8">
            <SectionHeader icon={MessageSquare} color="bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]" title="WhatsApp API" subtitle="Send messages and trigger campaigns from any external system — ecommerce, booking tools, or your own backend" />

            <p className="text-[#5C5C5C] text-base leading-relaxed font-medium max-w-3xl border-l-4 border-[#CDEFC4] pl-6 py-2">
              A general-purpose messaging API on top of your connected WhatsApp Business account. Use it to send order updates, appointment reminders, one-off notifications, or launch a bulk marketing campaign to a list of recipients — it isn't tied to any single use case.
            </p>

            <div className="p-4 bg-[#FEF1E5] border border-[#FDE4C9] rounded-lg flex items-start gap-3">
              <Key size={18} className="text-[#DD7A01] mt-0.5 shrink-0" />
              <div>
                <p className="text-[13px] text-[#444444] font-medium">Authenticate with the <code className="bg-white px-1 rounded-lg border text-[#DD7A01] font-bold">x-api-key</code> header (your API key, shown in the sidebar) instead of a Bearer token — this is what external systems without a logged-in user should use. A Bearer JWT also works if the call is coming from your own backend.</p>
                <p className="text-[12px] text-[#706E6B] mt-1">Sending endpoints are rate-limited to 60 requests/minute per API key; read endpoints to 300/minute.</p>
              </div>
            </div>

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <EndpointRow method="POST" path="/v1/meta/messages/send" desc="Send a template or free-form text message" />
              <EndpointRow method="POST" path="/v1/meta/messages/send-flow" desc="Send an interactive WhatsApp Flow" />
              <EndpointRow method="GET" path="/v1/meta/messages/:id" desc="Get delivery status of a sent message" />
              <EndpointRow method="GET" path="/v1/meta/templates" desc="List approved message templates" />
              <EndpointRow method="POST" path="/v1/meta/campaigns/create" desc="Trigger a bulk campaign to a list of recipients" />
              <EndpointRow method="GET" path="/v1/meta/campaigns" desc="List campaigns" />
              <EndpointRow method="GET" path="/v1/meta/campaigns/:id" desc="Get a campaign's status and delivery stats" />
              <EndpointRow method="POST" path="/v1/meta/campaigns/:id/contacts" desc="Append recipients to an existing campaign" />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#2E2E2E] flex items-center gap-3">
                <Terminal size={18} className="text-[#706E6B]" /> Send Message — POST /v1/meta/messages/send
              </h3>
              <ParamsTable rows={[
                ['recipient', 'String', true, 'Recipient phone number, digits only, international format (e.g. 15551234567)'],
                ['type', 'Enum', false, 'template | text — defaults to template'],
                ['templateName', 'String', true, 'Required when type=template. Must be an APPROVED template (see GET /v1/meta/templates)'],
                ['language', 'String', false, 'Template language code, defaults to en_US'],
                ['components', 'Array', false, 'Template variable values — same component shape as the WhatsApp Cloud API'],
                ['text', 'String', true, 'Required when type=text. Only deliverable inside the 24h customer service window'],
              ]} />
              <CodeTabs examples={{
                'cURL': `curl -X POST ${BASE}/v1/meta/messages/send \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "recipient": "15551234567",
    "type": "template",
    "templateName": "order_shipped",
    "language": "en_US",
    "components": [{ "type": "body", "parameters": [{ "type": "text", "text": "#A1002" }] }]
  }'`,
                'Node.js': `const axios = require('axios');

await axios.post('${BASE}/v1/meta/messages/send', {
  recipient: '15551234567',
  type: 'template',
  templateName: 'order_shipped',
  language: 'en_US',
  components: [{ type: 'body', parameters: [{ type: 'text', text: '#A1002' }] }]
}, {
  headers: { 'x-api-key': 'YOUR_API_KEY' }
});`,
              }} />
              <ResponseBox json={{ success: true, data: { _id: '64k9…', recipient: '15551234567', messageType: 'template', templateName: 'order_shipped', status: 'sent', sentAt: '2026-07-12T10:00:00Z' } }} />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#2E2E2E]">Trigger Campaign — POST /v1/meta/campaigns/create</h3>
              <ParamsTable rows={[
                ['name', 'String', true, 'Campaign name'],
                ['templateName', 'String', true, 'Must be an APPROVED template'],
                ['language', 'String', false, 'Template language code, defaults to en_US'],
                ['contacts', 'Array', false, 'List of { phoneNumber, variables } recipients to send to'],
              ]} />
              <ResponseBox json={{ success: true, data: { _id: '64l0…', name: 'Flash Sale Blast', status: 'processing', stats: { total: 500, sent: 0, delivered: 0, read: 0, failed: 0 } } }} />
            </div>

            <TryItPanel method="GET" path="/v1/meta/templates" />
          </section>

          {/* Errors */}
          <section ref={sec('errors')} className="space-y-8 pb-32">
            <SectionHeader icon={AlertCircle} color="bg-[#FEF1EE] text-[#BA0517] border-[#FDDDE3]" title="Status & Response Codes" subtitle="Standard HTTP error codes returned by the CRM API" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { code: '200', label: 'Success', color: 'text-[#2E844A]', desc: 'Request completed successfully. Response body contains the result data.' },
                { code: '201', label: 'Created', color: 'text-[#2E844A]', desc: 'New resource created successfully (POST requests).' },
                { code: '400', label: 'Bad Request', color: 'text-[#BA0517]', desc: 'Invalid parameters or malformed JSON payload in the request body.' },
                { code: '401', label: 'Unauthorized', color: 'text-[#DD7A01]', desc: 'Missing or expired JWT token. Re-authenticate to get a fresh token.' },
                { code: '403', label: 'Forbidden', color: 'text-[#BA0517]', desc: 'Valid token but insufficient permissions for this resource.' },
                { code: '404', label: 'Not Found', color: 'text-[#706E6B]', desc: 'The requested record does not exist or does not belong to your company.' },
                { code: '500', label: 'Server Error', color: 'text-[#706E6B]', desc: 'Internal server failure. Our crash recovery system has been notified.' },
              ].map((err, i) => (
                <Card key={i} className="p-6 flex items-start gap-6 hover:bg-[#F7F9FB] transition-colors border-[#E5E5E5] shadow-sm">
                  <div className={cn("text-4xl font-bold font-mono tracking-tighter shrink-0", err.color)}>
                    {err.code}
                  </div>
                  <div className="space-y-1">
                    <div className="text-[14px] font-bold text-[#2E2E2E] uppercase tracking-tight">{err.label}</div>
                    <p className="text-[12px] text-[#706E6B] font-medium leading-relaxed">{err.desc}</p>
                  </div>
                </Card>
              ))}
            </div>

            <ResponseBox json={{ success: false, message: 'Unauthorized: Please provide a valid Bearer token', code: 401 }} />
          </section>

        </div>
      </div>
    </DashboardModuleShell>
  );
}
